// Gestes : la chorégraphie de chaque section (jalon 4c), choisie avec Stefano sur maquettes. Un
// geste est un effet data-motion propre à un bloc ; apparitions.ts les pose avec les effets
// communs. Leur rythme (les temps qui n'appartiennent qu'à eux) vit ici ; les durées, courbes et
// seuils communs viennent de reglages.ts.
//
//   heros     quatre volets aux couleurs des chapitres se lèvent sur la photo
//   soulignes les mots qui nomment un chapitre se soulignent, puis se remplissent un instant
//   tiroir    le filet se trace, le contenu descend de sous lui (studios)
//   palettes  les marques se brouillent puis se fixent, comme un tableau d'affichage
//   palmares  les prix arrivent un à un, en zigzag d'une rangée à l'autre, et leur nombre compte
//   grille    les repères des colonnes, les filets par rangée, puis chaque module (Jolifish)
//   parc      un carré par site se pose, au hasard
//   paquet    la chaîne construite, un carré turquoise la parcourt une fois
//   couches   les phrases d'un paragraphe se posent l'une après l'autre (l'assistant vocal)
//   fiche     les rangées des compétences se remplissent, la clé puis la valeur
//   frappe    l'adresse se tape lettre à lettre
//
// Comme les effets communs : au repos, rien ne reste en ligne, tout découpage est défait, tout
// calque est retiré ; en mouvement réduit, de courts fondus, ou rien.

import { gsap } from 'gsap';
import { ScrambleTextPlugin } from 'gsap/ScrambleTextPlugin';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import {
  calque,
  compteur,
  declencheur,
  decouper,
  depart,
  fondu,
  poser,
  quandPose,
  retirer,
  type Effet,
} from './outils.ts';
import { COURBE, DUREE, ECHELON, SEUIL } from './reglages.ts';

gsap.registerPlugin(ScrollTrigger, ScrambleTextPlugin);

/** Écart entre deux prix qui arrivent : la liste prend son temps, chaque nombre se lit. */
const PAS_PRIX = ECHELON * 1.5;

/** Hasard reproductible : deux passages du même geste se ressemblent. */
function graine(n: number) {
  let s = n;
  return () => {
    s = (s * 16807) % 2147483647;
    return s / 2147483647;
  };
}

/** Les rangées d'éléments, regroupés par leur haut à l'écran. */
function rangees(els: Element[]): Element[][] {
  const r = new Map<number, Element[]>();
  for (const e of els) {
    const y = Math.round(e.getBoundingClientRect().top);
    const cle = [...r.keys()].find((k) => Math.abs(k - y) < 4) ?? y;
    r.set(cle, [...(r.get(cle) ?? []), e]);
  }
  return [...r.entries()].sort(([a], [b]) => a - b).map(([, v]) => v);
}

export const gestes: Record<string, Effet> = {
  // Avant le script, la photo attend, cachée (mouvement.scss) : les volets la couvrent dès qu'elle
  // se montre, rien ne clignote.
  heros(el, plein) {
    const photo = el.querySelector('img');
    if (!photo || !plein) return;
    const volets = calque('div', 'volets', el);
    for (let i = 0; i < 4; i++) volets.append(document.createElement('i'));
    gsap.fromTo(
      photo,
      { scale: 1.1 },
      { scale: 1, duration: 2.4, ease: COURBE.arrivee, delay: 0.3, clearProps: 'transform' },
    );
    gsap.to(volets.children, {
      yPercent: -100,
      duration: 0.95,
      ease: 'expo.inOut',
      stagger: 0.08,
      delay: 0.3,
      onComplete: () => {
        retirer(volets);
      },
    });
  },

  // Le paragraphe lu (son haut passé au milieu de l'écran), chaque mot qui nomme un chapitre se
  // souligne d'un trait continu, d'une ligne à l'autre, comme à la plume ; arrivé au bout, sa
  // couleur le remplit un instant et redescend en trait. Les mots se suivent dans l'ordre de
  // lecture.
  soulignes(el, plein) {
    const mots = [...el.querySelectorAll<HTMLElement>('.souligne')];
    if (!plein || !mots.length) return;
    gsap.set(mots, { '--trait': 0, '--remplir': 0 });
    const tl = gsap.timeline({
      ...declencheur(el, 'top 55%'),
      onComplete: () => {
        gsap.set(mots, { clearProps: '--trait,--remplir,color' });
      },
    });
    let t = 0.1;
    for (const a of mots) {
      // Le trait avance à vitesse de plume : sa durée suit sa longueur, toutes lignes comprises.
      const longueur = [...a.getClientRects()].reduce((s, r) => s + r.width, 0);
      const trace = gsap.utils.clamp(0.45, 0.95, longueur / 520);
      const style = getComputedStyle(a);
      const repos = style.color;
      const rempli = style.getPropertyValue('--souligne-texte').trim() || repos;
      tl.to(a, { '--trait': 1, duration: trace, ease: 'power2.inOut' }, t)
        .to(a, { '--remplir': 1, color: rempli, duration: 0.22, ease: 'power2.out' }, t + trace)
        .to(
          a,
          { '--remplir': 0, color: repos, duration: 0.5, ease: 'power2.inOut' },
          t + trace + 0.32,
        );
      t += trace * 0.6 + 0.2;
    }
  },

  // Le contenu descend d'un bloc, de toute sa hauteur, rogné au filet : il sort de sous lui.
  tiroir(el, plein) {
    const contenu = [...el.children];
    if (!plein) {
      fondu(el, contenu);
      return;
    }
    const bas = () => {
      const d = el.lastElementChild;
      return d instanceof HTMLElement ? d.offsetTop + d.offsetHeight + 4 : 0;
    };
    // Le rognage déborde sur les côtés et dessous, pour les jambages ; le haut est le filet.
    gsap.set(el, { '--trace': 0, clipPath: 'inset(0px -40px -80px -40px)' });
    gsap
      .timeline({ ...declencheur(el) })
      .to(el, { '--trace': 1, duration: DUREE.pose, ease: COURBE.arrivee, clearProps: '--trace' })
      .fromTo(
        contenu,
        { y: () => -bas() },
        { y: 0, duration: DUREE.entree, ease: COURBE.arrivee, clearProps: 'transform' },
        DUREE.pose * 0.3,
      )
      .set(el, { clearProps: 'clipPath' })
      .call(() => {
        poser(el);
      });
  },

  // Chaque nom garde sa largeur finale pendant qu'il se brouille : rien ne bouge autour de lui.
  // Caché dès le montage, il ne paraît que brouillé : le nom se lit quand il se fixe, pas avant.
  // Son carré se pose quand il se fixe.
  palettes(el, plein) {
    const noms = [...el.children];
    if (!plein) {
      fondu(el, noms);
      return;
    }
    const textes = noms.map((n) => n.textContent);
    gsap.set(noms, { '--carre': 0, opacity: 0 });
    const tl = gsap.timeline({ ...declencheur(el) });
    tl.call(() => {
      for (const n of noms)
        gsap.set(n, {
          width: n.getBoundingClientRect().width,
          whiteSpace: 'nowrap',
          clipPath: 'inset(-0.4em 0 -0.4em 0)',
        });
    });
    noms.forEach((n, i) => {
      tl.to(
        n,
        {
          duration: 0.8 + (i % 3) * 0.12,
          ease: 'none',
          scrambleText: {
            text: textes[i] ?? '',
            chars: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
            revealDelay: 0.35,
            speed: 0.5,
          },
          onStart: () => {
            gsap.set(n, { clearProps: 'opacity' });
          },
          onComplete: () => {
            gsap.set(n, { clearProps: 'width,whiteSpace,clipPath' });
          },
        },
        0.02 + i * 0.07,
      ).to(
        n,
        { '--carre': 1, duration: DUREE.pose, ease: COURBE.arrivee, clearProps: '--carre' },
        '<0.5',
      );
    });
  },

  // Les prix arrivent un à un, en croisant le petit côté de la grille : sur ordi (7 colonnes,
  // 2 rangées), le haut puis le bas de chaque colonne, de gauche à droite ; sur téléphone
  // (3 colonnes), chaque rangée de gauche à droite. Chacun monte, puis son nombre compte. L'ordre
  // se lit au déclenchement, dans la mise en page du moment.
  palmares(el, plein) {
    const prix = [...el.children];
    if (!plein) {
      fondu(el, prix);
      return;
    }
    const comptes = prix.map((p) => {
      const n = p.querySelector('b');
      return n ? compteur(n) : undefined;
    });
    gsap.set(prix, { opacity: 0 });
    for (const c of comptes) c?.ecrire(0);
    const jouer = () => {
      const place = new Map(
        prix.map((p) => {
          const r = p.getBoundingClientRect();
          return [p, { x: Math.round(r.left), y: Math.round(r.top) }];
        }),
      );
      const colonnes = new Set([...place.values()].map((p) => p.x)).size;
      const rangees = new Set([...place.values()].map((p) => p.y)).size;
      const parColonne = colonnes >= rangees;
      const ordre = [...prix].sort((a, b) => {
        const pa = place.get(a);
        const pb = place.get(b);
        if (!pa || !pb) return 0;
        return parColonne ? pa.x - pb.x || pa.y - pb.y : pa.y - pb.y || pa.x - pb.x;
      });
      const tl = gsap.timeline();
      ordre.forEach((p, i) => {
        const t = i * PAS_PRIX;
        tl.fromTo(
          p,
          { y: '0.5em', opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: DUREE.entree,
            ease: COURBE.arrivee,
            clearProps: 'transform,opacity',
          },
          t,
        );
        const c = comptes[prix.indexOf(p)];
        if (!c) return;
        const valeur = { v: 0 };
        tl.to(
          valeur,
          {
            v: c.cible,
            duration: DUREE.entree * 1.2,
            ease: COURBE.douce,
            onUpdate: () => {
              c.ecrire(valeur.v);
            },
          },
          t + 0.1,
        );
      });
    };
    const { scrollTrigger: st } = declencheur(el);
    if (st) ScrollTrigger.create({ ...st, onEnter: jouer });
    else jouer();
  },

  // Les repères tombent aux bords des colonnes, les filets se tracent par rangée, puis chaque
  // module se remplit ; les repères s'effacent.
  grille(el, plein) {
    const modules = [...el.children];
    const contenus = modules.flatMap((m) => [...m.children]);
    if (!plein) {
      fondu(el, contenus);
      return;
    }
    gsap.set(modules, { '--trace': 0 });
    gsap.set(contenus, { opacity: 0 });
    ScrollTrigger.create({
      trigger: el,
      start: 'clamp(top 80%)',
      once: true,
      onEnter: () => {
        el.classList.add('grille-active');
        const boite = el.getBoundingClientRect();
        const xs = new Set(
          modules.flatMap((m) => {
            const b = m.getBoundingClientRect();
            return [Math.round(b.left - boite.left), Math.round(b.right - boite.left)];
          }),
        );
        const reperes = calque('div', 'reperes', el);
        for (const x of xs) {
          const i = document.createElement('i');
          i.style.left = `${String(x)}px`;
          reperes.append(i);
        }
        const tl = gsap.timeline({
          onComplete: () => {
            retirer(reperes);
            el.classList.remove('grille-active');
          },
        });
        tl.fromTo(
          reperes.children,
          { scaleY: 0 },
          { scaleY: 1, duration: 0.9, ease: 'expo.inOut', stagger: 0.07 },
          0,
        );
        rangees(modules).forEach((r, k) => {
          tl.to(
            r,
            { '--trace': 1, duration: DUREE.pose, ease: COURBE.arrivee, clearProps: '--trace' },
            0.4 + k * 0.2,
          );
        });
        modules.forEach((m, k) => {
          tl.fromTo(
            [...m.children],
            { opacity: 0, y: 14 },
            {
              opacity: 1,
              y: 0,
              duration: DUREE.entree,
              ease: COURBE.arrivee,
              stagger: 0.08,
              clearProps: 'transform,opacity',
            },
            0.75 + k * 0.16,
          );
        });
        tl.to(reperes, { opacity: 0, duration: 0.5 }, '-=0.45');
      },
    });
  },

  parc(el, plein) {
    if (!plein) {
      fondu(el);
      return;
    }
    gsap.from(el.children, {
      scale: 0,
      duration: 0.5,
      ease: COURBE.arrivee,
      stagger: { each: 0.014, from: 'random' },
      delay: 0.2,
      clearProps: 'transform',
      ...declencheur(el),
    });
  },

  // La chaîne construite (elle le signale), le paquet part des contenus du client, traverse chaque
  // outil jusqu'aux visiteurs, puis fait l'aller-retour de l'administration. Le nœud qu'il
  // traverse s'allume un instant.
  paquet(el, plein) {
    if (!plein) return;
    const q = (n: string) => el.querySelector<HTMLElement>(`.chaine__${n}`);
    const jouer = () => {
      el.classList.add('paquet-actif');
      const boite = el.getBoundingClientRect();
      // Les deux bouts d'une flèche, dans son sens : à l'horizontale ou à la verticale selon la
      // mise en page (la chaîne se dresse sur téléphone).
      type Bouts = readonly [x0: number, y0: number, x1: number, y1: number];
      const bouts = (f: HTMLElement | null, retour = false): Bouts | null => {
        if (!f) return null;
        const r = f.getBoundingClientRect();
        const [x, y] = [r.left - boite.left, r.top - boite.top];
        const [x0, y0, x1, y1]: Bouts =
          r.width >= r.height
            ? [x, y + r.height / 2, x + r.width, y + r.height / 2]
            : [x + r.width / 2, y, x + r.width / 2, y + r.height];
        return retour ? [x1, y1, x0, y0] : [x0, y0, x1, y1];
      };
      const paquet = calque('div', 'paquet', el);
      const turq = getComputedStyle(document.documentElement).getPropertyValue('--turq').trim();
      const tl = gsap.timeline({
        onComplete: () => {
          retirer(paquet);
          el.classList.remove('paquet-actif');
        },
      });
      let t = 0;
      const fleche = (f: HTMLElement | null, retour = false) => {
        const p = bouts(f, retour);
        if (!p) return;
        const [x0, y0, x1, y1] = p;
        tl.set(paquet, { x: x0, y: y0, opacity: 1 }, t)
          .to(paquet, { x: x1, y: y1, duration: 0.32, ease: 'power1.inOut' }, t)
          .set(paquet, { opacity: 0 }, t + 0.32);
        t += 0.32;
      };
      const noeud = (n: HTMLElement | null, prop: 'borderColor' | 'backgroundColor' | 'color') => {
        if (!n) return;
        const avant = getComputedStyle(n)[prop];
        tl.to(n, { [prop]: turq, duration: 0.1 }, t).to(
          n,
          { [prop]: avant, duration: 0.5, clearProps: prop },
          t + 0.22,
        );
        t += 0.16;
      };
      fleche(q('fleche--1'));
      noeud(q('outil--gen'), 'borderColor');
      fleche(q('fleche--2'));
      noeud(q('site'), 'backgroundColor');
      fleche(q('fleche--3'));
      noeud(q('outil--public'), 'borderColor');
      fleche(q('fleche--4'));
      noeud(q('bout--sortie'), 'color');
      fleche(q('fleche--aller-retour'));
      noeud(q('outil--admin'), 'borderColor');
      fleche(q('fleche--aller-retour'), true);
      noeud(q('site'), 'backgroundColor');
    };
    quandPose(el, () => {
      gsap.delayedCall(0.15, jouer);
    });
  },

  // Les mots se groupent par phrase ; chaque phrase monte d'un peu plus que la précédente.
  couches(el, plein) {
    const bloc = el.closest('article') ?? el;
    if (!plein) {
      fondu(bloc, el);
      return;
    }
    gsap.set(el, { opacity: 0 });
    ScrollTrigger.create({
      trigger: bloc,
      start: SEUIL,
      once: true,
      onEnter: () => {
        const s = decouper(el, { type: 'words', wordsClass: 'mot' });
        gsap.set(el, { clearProps: 'opacity' });
        const phrases: Element[][] = [];
        let k = 0;
        for (const m of s.words) {
          (phrases[k] ??= []).push(m);
          if (/[.!?]$/.test(m.textContent)) k++;
        }
        const tl = gsap.timeline({
          delay: 0.3,
          onComplete: () => {
            s.revert();
          },
        });
        phrases.forEach((p, i) => {
          tl.from(
            p,
            { opacity: 0, y: 10 + i * 8, duration: DUREE.entree, ease: COURBE.arrivee },
            i * 0.42,
          );
        });
      },
    });
  },

  // Chaque colonne se remplit quand elle arrive aux deux tiers de l'écran : son titre, puis ses
  // rangées une à une, la clé d'abord, sa valeur juste après. Côte à côte, les trois colonnes
  // avancent ensemble ; sur une colonne, chacune à son tour.
  fiche(el, plein) {
    const pas = ECHELON * 2.2;
    for (const col of el.children) {
      const rangs: Element[][] = [];
      const titre = col.querySelector('h2');
      if (titre) rangs.push([titre]);
      for (const dt of col.querySelectorAll('dt'))
        rangs.push([dt, dt.nextElementSibling].filter((e): e is Element => e !== null));
      for (const li of col.querySelectorAll('li')) rangs.push([li]);
      const tl = gsap.timeline({ ...declencheur(col, 'clamp(top 68%)') });
      rangs.forEach((rang, i) => {
        rang.forEach((e, j) => {
          tl.from(
            e,
            {
              ...depart(plein, 14),
              duration: plein ? DUREE.entree : DUREE.etat,
              ease: COURBE.arrivee,
            },
            plein ? i * pas + j * 0.1 : 0,
          );
        });
      });
    }
  },

  // Le curseur, un carré turquoise, suit la dernière lettre tapée, au rythme irrégulier d'une
  // frappe, clignote, puis s'en va.
  frappe(el, plein) {
    if (!plein) {
      fondu(el);
      return;
    }
    gsap.set(el, { opacity: 0 });
    ScrollTrigger.create({
      trigger: el,
      start: SEUIL,
      once: true,
      onEnter: () => {
        // Découpée, l'adresse perd l'approche de ses paires de lettres et s'élargit de quelques
        // pixels : là où elle tient tout juste sur une ligne au repos (375 à 393 px de large), elle
        // passerait à la ligne le temps de la frappe. Elle garde alors sa ligne.
        const texte = document.createRange();
        texte.selectNodeContents(el);
        const uneLigne = texte.getClientRects().length === 1;
        const s = decouper(el, { type: 'chars', charsClass: 'lettre' });
        el.classList.toggle('frappe-ligne', uneLigne);
        gsap.set(el, { clearProps: 'opacity' });
        gsap.set(s.chars, { opacity: 0 });
        const tl = gsap.timeline({
          onComplete: () => {
            s.revert();
            el.classList.remove('frappe-ligne');
          },
        });
        const hasard = graine(7);
        let t = 0.1;
        for (const c of s.chars) {
          tl.call(
            () => {
              for (const x of s.chars) x.classList.toggle('mot--curseur', x === c);
            },
            [],
            t,
          ).set(c, { opacity: 1 }, t);
          t += 0.035 + hasard() * 0.055;
        }
        const fin = s.chars.at(-1);
        if (fin)
          tl.to(
            fin,
            { '--curseur': 0, duration: 0.01, repeat: 3, yoyo: true, repeatDelay: 0.36 },
            '+=0.3',
          );
      },
    });
  },
};
